import { useEffect } from "react";
import { useAuth } from "react-oidc-context";

import { LAST_ROUTE, routes } from "../constants";
import { UserDataProvider } from "../contexts/userDataContext";
import { ServerError } from "../pages/authPages";
import {
	getAppTitle,
	getClientId,
	getDebugInfo,
	getIdAuthority,
	getMockMode,
	getResponseType,
} from "../utils";
import { Preloader } from "./preloader";

// Session-storage flag guarding the OIDC self-heal redirect (see error branch).
const OIDC_RETRY = "oidc_retry";

const getParentDomain = (url: string) =>
	new URL(url).hostname.split(".").slice(-2).join(".");

// OIDC authority (Identity Server origin) resolved from runtime config by origin.
const authority = getIdAuthority() ?? "";

// Single source of truth for the config shape. `sameDomain` is the only thing
// that varies between the synchronous best-guess and the resolved version.
const makeConfig = (sameDomain: boolean) => ({
	authority,
	client_id: getClientId() ?? "",
	redirect_uri: window.location.origin + routes.signInOidc.path,
	client_secret: (getAppTitle() || "") + (getDebugInfo() || ""),
	response_type: getResponseType(),
	scope: "openid crmApi blastDataApi IdentityServerApi offline_access",
	grant_type: "code",
	post_logout_redirect_uri:
		window.location.origin + routes.signOutCallbackOidc.path,
	// automaticSilentRenew + monitorSession use iframes against the OP's own
	// endpoints, so they only work when the OP shares a parent domain with us.
	automaticSilentRenew: sameDomain,
	monitorSession: sameDomain,
});

// Synchronous best-guess based on the configured authority. Used until
// initOidcConfig() resolves the real issuer, so any early import has a value.
export let oidcConfig = makeConfig(
	!!authority &&
		getParentDomain(authority) === getParentDomain(window.location.origin),
);

// Resolve `sameDomain` off the OP's ACTUAL host (its discovery `issuer`) rather
// than the authority string we configured — that issuer is where the silent-renew
// and session-check iframes load from, so it's what cross-site really hinges on.
// Call once at startup BEFORE rendering <AuthProvider> / constructing the UserManager.
export const initOidcConfig = async () => {
	const sameAsAuthority =
		!!authority &&
		getParentDomain(authority) === getParentDomain(window.location.origin);

	// // .com hosts keep the old behaviour: sameDomain straight off the authority,
	// // no discovery round-trip.
	// if (window.location.origin.includes(".com")) {
	// 	oidcConfig = makeConfig(sameAsAuthority);
	// 	return oidcConfig;
	// }

	// everything else resolves sameDomain from the OP's real issuer.
	let opOrigin = authority;
	try {
		const meta = await fetch(
			`${authority}/.well-known/openid-configuration`,
		).then((r) => r.json());
		opOrigin = meta.issuer || authority;
	} catch {
		// discovery unreachable / not JSON — keep the authority-based guess
	}

	const sameDomain =
		!!opOrigin &&
		getParentDomain(opOrigin) === getParentDomain(window.location.origin);

	oidcConfig = makeConfig(sameDomain);
	console.log("Monitor Session :>> ", sameDomain);
	return oidcConfig;
};

export const Auth = ({
	children,
	noRefresh = false,
	isReturnIfNotAuth = false,
	isWaitForUser = false,
	isRegister = false,
}: {
	children: any;
	noRefresh?: boolean;
	isReturnIfNotAuth?: boolean;
	isWaitForUser?: boolean;
	isRegister?: boolean;
}) => {
	const {
		isAuthenticated,
		activeNavigator,
		isLoading,
		error,
		events,
		signinRedirect,
		signinSilent,
		removeUser,
	} = useAuth();

	useEffect(() => {
		// the `return` is important - addAccessTokenExpiring() returns a cleanup function
		return events.addAccessTokenExpiring(async () => {
			console.log("addAccessTokenExpiring");
			try {
				await signinSilent();
			} catch (error) {
				console.error(error);
				window.location.href = "/";
			}
		});
	}, [events, signinSilent]);

	useEffect(() => {
		// the `return` is important - addUserSignedOut() returns a cleanup function
		return events.addUserSignedOut(() => {
			console.log("addUserSignedOut");
			// https://github.com/IdentityModel/oidc-client-js/issues/729
			// band aid fix so we don't have to do weird cookie nonsense
			if (
				!import.meta.env.DEV &&
				!window.location.pathname.includes("/signin-oidc")
			) {
				console.log("removing user");
				removeUser();
			}
		});
	}, [events, removeUser]);

	useEffect(() => {
		// the `return` is important - addUserSignedIn() returns a cleanup function
		return events.addUserSignedIn(() => {
			console.log("addUserSignedIn");
			if (!window.location.pathname.includes("/signin-oidc")) {
				console.log("signed in from another tab, removing user");
				removeUser();
			}
		});
	}, [events, removeUser]);

	useEffect(() => {
		// A completed sign-in clears the self-heal retry guard below.
		if (isAuthenticated) sessionStorage.removeItem(OIDC_RETRY);
	}, [isAuthenticated]);

	if (
		isAuthenticated ||
		isReturnIfNotAuth ||
		import.meta.env.MODE === "test" ||
		getMockMode()
	) {
		return (
			<UserDataProvider
				isWaitForUser={isWaitForUser}
				isReturnIfNotAuth={isReturnIfNotAuth}
			>
				{children}
			</UserDataProvider>
		);
	}

	if (isLoading) {
		return <Preloader />;
	}

	switch (activeNavigator) {
		case "signinSilent":
			return <Preloader />;
		case "signoutRedirect":
			return <Preloader />;
		default:
			break;
	}

	if (error) {
		console.log("error :>> ", error);
		// "No matching state found in storage" is recoverable — the sign-in
		// state was already consumed or lost (duplicate callback, back-button,
		// or a cross-tab login). Restart the flow once rather than dead-ending
		// on the error screen. The retry guard stops an infinite redirect loop
		// if sign-in genuinely can't complete; it's cleared on success above.
		if (error.message?.includes("No matching state")) {
			if (!sessionStorage.getItem(OIDC_RETRY)) {
				sessionStorage.setItem(OIDC_RETRY, "1");
				signinRedirect();
				return <Preloader />;
			}
		}
		return <ServerError message={error.message} error={error} />;
	}

	if (noRefresh) {
		return null;
	}

	sessionStorage.setItem(LAST_ROUTE, window.location.pathname);
	signinRedirect({
		extraQueryParams: {
			register: isRegister,
		},
	});
	return null;
};
